<template>
    <v-menu
        v-model="menu_open"
        content-class="overflow-y-hidden dropmenu pa-1 elevation-0"
        left
        nudge-right="50"
        offset-y
        attach
    >
        <template v-slot:activator="{ on }">
            <slot :on="on" />
        </template>
        <v-card class="date-range">
            <v-row
                no-gutters
                justify="start"
                align="start"
            >
                <v-col style="min-width: 242px">
                    <v-card-title class="ma-0 py-2 px-4">
                        <span class="font-weight-regular">
                            {{ $t("Snooze until...") }}</span>
                    </v-card-title>
                    <template>
                        <v-list>
                            <v-list-item
                                v-for="(mitem, index) in filtered_snooze_menu"
                                :key="index"
                                class="menu-list-item"
                                dense
                                @click.stop="mitem.action()"
                            >
                                <v-list-item-content>
                                    <v-list-item-title>
                                        {{ mitem.title }}
                                    </v-list-item-title>
                                </v-list-item-content>
                                <v-list-item-action>
                                    <span
                                        class="g5--text body-2"
                                    >
                                        {{ mitem.date }}
                                    </span>
                                </v-list-item-action>
                            </v-list-item>
                        </v-list>

                        <v-list-item
                            class="menu-list-item custom-date py-2"
                            dense
                            @click.stop="onSnoozeCustomClick"
                        >
                            <v-list-item-icon class="mr-4 mt-0">
                                <icon icon="CalendarDays" />
                            </v-list-item-icon>
                            <v-list-item-content>
                                <v-list-item-title class="body-2">
                                    {{ $t("Select date & time") }}
                                </v-list-item-title>
                            </v-list-item-content>
                        </v-list-item>
                    </template>
                </v-col>
            </v-row>
        </v-card>
    </v-menu>
</template>

<script>
import { ref, computed } from "@vue/composition-api";
import TaskModule from "@/store/modules/task";
import { $t } from "@/i18n";
import { actions as dialogActions } from "@/store/modules/dialog";
import {
    formatWeekdayShort,
    calcDateEquals,
    parseSysDateTime,
    formatTime
} from "@/plugins/formatting";

export default {
    name: "BulkTaskSnoozeMenu",
    props: {
        btnClass: {
            type: String,
            required: false,
            default: ""
        },
        inTaskForm: {
            type: Boolean,
            default: false
        }
    },
    setup(props, ctx) {
        const now = new Date();
        const { getTaskId, snoozeTasks } = TaskModule();
        const menu_open = ref(false);

        const formatDateTimeFriendly = function(d) {
            if (!d) return "";
            if (typeof d == "string") d = parseSysDateTime(d);

            if (calcDateEquals(now, d, "date")) {
                return formatTime(d);
            } else {
                return formatWeekdayShort(d) + ", " + formatTime(d);
            }
        };
        const tomorrow = () => {
            let s = new Date();
            s = s.calcAddDays(1);
            s.setHours(8, 0, 0);
            return s;
        };
        const later_this_week = () => {
            let s = new Date();
            let day = s.getDay() === 0 ? 6 : s.getDay() - 1;
            if ([0, 1, 2].includes(day)) {
                s = s.calcAddDays(3 - day); // thursday
                s.setHours(8, 0, 0);
                return s;
            } else if (day === 3) {
                s = s.calcAddDays(4 - day); // friday
                s.setHours(8, 0, 0);
                return s;
            }
            return null;
        };
        const next_week = () => {
            let s = now;
            s = s.calcWeekStart().calcDateAdd(1, "week");
            s.setHours(8, 0, 0);
            return s;
        };
        function saveAction(val) {
            snoozeTasks({
                date: val.formatSysDateTime(),
                due_date: val.formatSysDateTime()
            });
        }
        const snooze_menu = ref([
            // 0 = Monday
            // 6 = Sunday
            {
                icon: "mdi-brightness-5",
                title: $t("Tomorrow"),
                type: "tomorrow",
                date: formatDateTimeFriendly(tomorrow()),
                action: () => saveAction(tomorrow()),
                displayDays: [0, 1, 2, 3, 4, 5, 6]
            },
            {
                icon: "mdi-briefcase",
                title: $t("Later this week"),
                type: "later_this_week",
                date: formatDateTimeFriendly(later_this_week()),
                action: () => saveAction(later_this_week()),
                displayDays: [0, 1, 2, 3]
            },
            {
                icon: "mdi-briefcase",
                title: $t("Next week"),
                type: "next_week",
                date: formatDateTimeFriendly(next_week()),
                action: () => saveAction(next_week()),
                displayDays: [0, 1, 2, 3, 4, 5, 6]
            }
        ]);
        const filtered_snooze_menu = computed(function() {
            return snooze_menu.value.filter(function(m) {
                let day = now.getDay() === 0 ? 6 : now.getDay() - 1;
                return m["displayDays"].includes(day);
            });
        });

        const onSnoozeCustomClick = () => {
            if (props.inTaskForm) {
                ctx.emit("openDatePicker");
                menu_open.value = false;
            } else {
                dialogActions.setDialogOptions({
                    component: () => import("@/views/Dialog/DateAndTimeDialog"),
                    width: 358,
                    payload: {},
                    action: saveAction
                });
                dialogActions.openDialog();
            }
        };

        return {
            snooze_menu,
            onSnoozeCustomClick,
            formatDateTimeFriendly,
            filtered_snooze_menu,
            getTaskId,
            menu_open
        };
    }
};
</script>

<style scoped>
.custom-date {
    min-height: 2.5rem !important;
    border-top: 1px solid lightgrey;
}
</style>
